import { CircleAlert, CircleCheck, Info, TriangleAlert, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/shared/lib/cn";

export type AlertTone = "info" | "success" | "warning" | "danger";

const TONES: Record<AlertTone, { icon: LucideIcon; box: string; accent: string }> = {
  info: { icon: Info, box: "border-info/20 bg-info-soft", accent: "text-info" },
  success: { icon: CircleCheck, box: "border-success/20 bg-success-soft", accent: "text-success" },
  warning: {
    icon: TriangleAlert,
    box: "border-warning/25 bg-warning-soft",
    accent: "text-warning",
  },
  danger: { icon: CircleAlert, box: "border-danger/25 bg-danger-soft", accent: "text-danger" },
};

interface InlineAlertProps {
  tone: AlertTone;
  title?: string;
  children?: ReactNode;
  /** A control at the end, such as "Try again". */
  action?: ReactNode;
  className?: string;
}

/**
 * A message inside the page or a form: a failed load, a rejected submission, a heads-up.
 * Only danger interrupts screen readers (role="alert"); the other tones are read in place.
 */
export function InlineAlert({ tone, title, children, action, className }: InlineAlertProps) {
  const { icon: Icon, box, accent } = TONES[tone];
  return (
    <div
      role={tone === "danger" ? "alert" : undefined}
      className={cn(
        "flex flex-wrap items-start gap-x-2.5 gap-y-2 rounded-lg border px-3 py-2.5 text-[14px] leading-relaxed",
        box,
        className,
      )}
    >
      <Icon className={cn("mt-[3px] size-4 shrink-0", accent)} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        {title ? <p className={cn("font-medium", accent)}>{title}</p> : null}
        {children ? (
          <div className={title ? "text-ink-muted" : "font-medium text-ink"}>{children}</div>
        ) : null}
      </div>
      {action ? <div className="ms-auto shrink-0 self-center">{action}</div> : null}
    </div>
  );
}
